import type { JSX } from "react";
import { useState } from "react";

import { formatFileNameForExport } from "@impero-com/common/utils/formatting";
import { useTranslation } from "@impero-com/common/utils/i18n";
import { notifyRequestError } from "@impero-com/common/utils/query_client";
import { Modal } from "@impero-com/ui";
import { Checkbox } from "antd";
import saveAs from "save-as";

import { legacyControlProgramExport } from "api/legacy/legacy-control-program/legacy-control-program";
import { LegacyControlFieldName } from "api/legacy/model/legacyControlFieldName";
import { Footer } from "components/modals/Footer";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";

import { exportOptions } from "./ExportProgramDialog.css";

interface ExportProgramDialogProps {
    controlProgramId: number;
    onClose: () => void;
}

/** Dialog mirroring the legacy ExportControlProgramDialog */
export function ExportProgramDialog({
    controlProgramId,
    onClose,
}: ExportProgramDialogProps): JSX.Element {
    const { t } = useTranslation();
    const controlEntitiesEnabled = useFeatureFlag("controlEntities");

    const [includeTasks, setIncludeTasks] = useState(false);
    const [includeRisks, setIncludeRisks] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const handleExport = async () => {
        // Clone of the column list the v1 dialog
        // (Scripts/Components/controlProgramExportDialog) hardcodes
        const fieldNames: LegacyControlFieldName[] = [
            LegacyControlFieldName.ControlProgram,
            LegacyControlFieldName.Title,
            LegacyControlFieldName.Description,
            LegacyControlFieldName.ControlTags,
            LegacyControlFieldName.Due,
            LegacyControlFieldName.Reminders,
            LegacyControlFieldName.LoginProtectionEnabled,
            LegacyControlFieldName.Assignee,
            LegacyControlFieldName.AssigneeGroup,
            LegacyControlFieldName.AssigneeTags,
            LegacyControlFieldName.Reviewer,
            ...(controlEntitiesEnabled ? [LegacyControlFieldName.Entity] : []),
            LegacyControlFieldName.DefaultClaimant,
            LegacyControlFieldName.NotificationRecipients,
            LegacyControlFieldName.SegregationOfDutiesEnforced,
            LegacyControlFieldName.LastUpdated,
            LegacyControlFieldName.LastUpdatedBy,
        ];

        setIsExporting(true);
        try {
            const response = await legacyControlProgramExport(
                controlProgramId,
                { fieldNames, includeTasks, includeRisks }
            );
            saveAs(response.data, formatFileNameForExport("programs", "xlsx"));
            onClose();
        } catch (error) {
            notifyRequestError(error);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Modal
            variant="content"
            open
            title={t("ControlProgram_ExportOptions_Title")}
            footer={
                <Footer
                    onClose={onClose}
                    onConfirm={() => void handleExport()}
                    confirmLabel={t("Export")}
                    loading={isExporting}
                />
            }
            onClose={onClose}
            hideCloseButton={isExporting}
            disableDismiss={isExporting}
        >
            <div className={exportOptions}>
                <div>
                    <Checkbox
                        checked={includeTasks}
                        onChange={(e) => setIncludeTasks(e.target.checked)}
                    >
                        {t("ControlProgram_ExportOptions_WithTasks")}
                    </Checkbox>
                </div>
                <div>
                    <Checkbox
                        checked={includeRisks}
                        onChange={(e) => setIncludeRisks(e.target.checked)}
                    >
                        {t("ControlProgram_ExportOptions_WithRisks")}
                    </Checkbox>
                </div>
            </div>
        </Modal>
    );
}
